import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { STATUS_META, BRAND_COLOR, formatLeadDateTime, isReportableStatus } from "../constants";

interface LeadsTableProps {
  data: LeadsManagementData;
}

const SORTABLE_COLUMNS: Array<{ field: "id" | "name" | "brand" | "status" | "industry" | "amount" | "date"; label: string }> = [
  { field: "id", label: "ID" },
  { field: "name", label: "Full Name" },
  { field: "brand", label: "Brand Name" },
  { field: "status", label: "Status" },
  { field: "industry", label: "Industry" },
  { field: "amount", label: "Borrow Amount" },
  { field: "date", label: "Date of Acquisition" },
];

export default function LeadsTable({ data }: LeadsTableProps) {
  const {
    pageLeads,
    totalFiltered,
    pageStartIndex,
    perPage,
    setPerPage,
    pageSizeOptions,
    page,
    totalPages,
    allChecked,
    selected,
    sortField,
    sortDirection,
    toggleSort,
    toggleAllReportable,
    toggleLeadSelected,
    openLeadDetail,
    goToPrevPage,
    goToNextPage,
  } = data;

  return (
    <Paper variant="outlined" sx={{ mt: 2.25, borderRadius: 2, overflow: "hidden" }}>
      <TableContainer sx={{ minWidth: 900 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allChecked}
                  onChange={toggleAllReportable}
                  sx={{ color: BRAND_COLOR, "&.Mui-checked": { color: BRAND_COLOR } }}
                />
              </TableCell>
              {SORTABLE_COLUMNS.map((column) => (
                <TableCell key={column.field} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortField === column.field}
                    direction={sortField === column.field ? sortDirection : "asc"}
                    onClick={() => toggleSort(column.field)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {pageLeads.map((lead) => {
              const meta = STATUS_META[lead.status];
              const reportable = isReportableStatus(lead.status);
              return (
                <TableRow key={lead.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={!!selected[lead.id]}
                      disabled={!reportable}
                      onChange={() => toggleLeadSelected(lead.id)}
                      data-tracker="928"
                      sx={{ color: BRAND_COLOR, "&.Mui-checked": { color: BRAND_COLOR } }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#616161" }}>{lead.id}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212121" }}>
                      {lead.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#9e9e9e" }}>{lead.email}</Typography>
                  </TableCell>
                  <TableCell sx={{ color: "#424242" }}>{lead.brand}</TableCell>
                  <TableCell>
                    <Chip
                      label={meta.label}
                      size="small"
                      sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#616161" }}>{lead.industry}</TableCell>
                  <TableCell sx={{ color: "#616161" }}>{lead.amount}</TableCell>
                  <TableCell sx={{ color: "#757575", fontSize: 13.5 }}>
                    {formatLeadDateTime(lead.date)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View lead">
                      <IconButton
                        size="small"
                        onClick={() => openLeadDetail(lead.id)}
                        data-tracker="929"
                        sx={{ color: BRAND_COLOR }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalFiltered === 0 && (
        <Box sx={{ p: 6, textAlign: "center", color: "#9e9e9e", fontSize: 15 }}>
          No leads match the current filters.
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.75,
          px: 2.25,
          py: 1.75,
          borderTop: "1px solid #f2f2f2",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 13.5, color: "#757575", lineHeight: "20px" }}>
            Rows per page:
          </Typography>
          <Select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            size="small"
            variant="standard"
            disableUnderline
            sx={{
              fontSize: 13.5,
              color: "#757575",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                py: 0,
                lineHeight: "20px",
              },
            }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: 13.5 }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography sx={{ fontSize: 13.5, color: "#757575" }}>
          {totalFiltered === 0 ? 0 : pageStartIndex + 1}–{Math.min(pageStartIndex + perPage, totalFiltered)} of {totalFiltered}
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.25 }}>
          <IconButton size="small" onClick={goToPrevPage} sx={{ color: BRAND_COLOR }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ fontSize: 13.5, color: "#616161" }}>
            {page} / {totalPages}
          </Typography>
          <IconButton size="small" onClick={goToNextPage} sx={{ color: BRAND_COLOR }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
}
